/**
 * Account Metadata Management
 * Utilities for managing account metadata and persistence settings
 */

import { StoredAccount } from './accountStorage';
import { LoginPersistenceOptions } from './authSecretUtils';

export interface AccountMetadata {
  id: string;
  email: string;
  displayName: string;
  profilePicture?: string;
  lastUsed: number;
  createdAt: number;
  loginCount: number;
  persistenceSettings: LoginPersistenceOptions;
  tags?: string[];
  notes?: string;
}

export interface AccountUsageStats {
  accountId: string;
  totalLogins: number;
  lastLogin: number;
  averageSessionDuration: number;
  mostUsedFeatures: string[];
  deviceInfo?: {
    userAgent: string;
    platform: string;
    lastSeen: number;
  };
}

/**
 * Account metadata helper functions
 */
export class AccountMetadataHelper {
  
  /**
   * Extract metadata from stored account
   */
  static extractMetadata(account: StoredAccount): AccountMetadata {
    return {
      id: account.id,
      email: account.email,
      displayName: account.userName || account.loginName,
      profilePicture: account.profilePicture,
      lastUsed: account.lastUsed,
      createdAt: this.extractCreatedAt(account.id),
      loginCount: 1, // This would be tracked separately in a real implementation
      persistenceSettings: {
        rememberMe: account.rememberMe,
        persistenceDays: account.persistenceDays
      }
    };
  }

  /**
   * Update account metadata
   */
  static updateMetadata(
    account: StoredAccount, 
    updates: Partial<AccountMetadata>
  ): StoredAccount {
    const updatedAccount = { ...account };

    if (updates.displayName && updates.displayName !== account.userName) {
      updatedAccount.userName = updates.displayName;
    }

    if (updates.profilePicture !== undefined) {
      updatedAccount.profilePicture = updates.profilePicture;
    }

    if (updates.persistenceSettings) {
      updatedAccount.rememberMe = updates.persistenceSettings.rememberMe;
      updatedAccount.persistenceDays = updates.persistenceSettings.persistenceDays || 1;
    }

    if (updates.lastUsed) {
      updatedAccount.lastUsed = updates.lastUsed;
    }

    return updatedAccount;
  }

  /**
   * Generate account display name
   */
  static generateDisplayName(account: StoredAccount): string {
    if (account.userName && account.userName !== account.loginName) {
      return account.userName;
    }

    // Extract name from email if possible
    if (account.email.includes('@')) {
      const localPart = account.email.split('@')[0];
      // Convert common email patterns to readable names
      return localPart
        .replace(/[._-]/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase());
    }

    return account.loginName;
  }

  /**
   * Generate account initials for profile picture fallback
   */
  static generateInitials(account: StoredAccount): string {
    const displayName = this.generateDisplayName(account);
    
    // Split by spaces and take first letter of each word
    const words = displayName.split(' ').filter(word => word.length > 0);
    
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    } else if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase();
    } else {
      // Fallback to email
      return account.email.substring(0, 2).toUpperCase();
    }
  }

  /**
   * Get account color for UI (based on account ID)
   */
  static getAccountColor(accountId: string): string {
    // Generate a consistent color based on account ID
    const colors = [
      '#3B82F6', // Blue
      '#10B981', // Green
      '#F59E0B', // Yellow
      '#EF4444', // Red
      '#8B5CF6', // Purple
      '#06B6D4', // Cyan
      '#F97316', // Orange
      '#84CC16', // Lime
      '#EC4899', // Pink
      '#6B7280'  // Gray
    ];

    let hash = 0;
    for (let i = 0; i < accountId.length; i++) {
      hash = accountId.charCodeAt(i) + ((hash << 5) - hash);
    }

    const index = Math.abs(hash) % colors.length;
    return colors[index];
  }

  /**
   * Extract created timestamp from account ID
   */
  private static extractCreatedAt(accountId: string): number {
    try {
      // Account ID format: acc_{hash}_{timestamp}
      const parts = accountId.split('_');
      if (parts.length >= 3) {
        const timestampBase36 = parts[2];
        return parseInt(timestampBase36, 36);
      }
    } catch (error) {
      console.warn('Failed to extract created timestamp from account ID:', error);
    }
    
    // Fallback to current time
    return Date.now();
  }

  /**
   * Format last used time for display
   */
  static formatLastUsed(lastUsed: number): string {
    const now = Date.now();
    const diffMs = now - lastUsed;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) {
      return 'Just now';
    } else if (diffMinutes < 60) {
      return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    } else {
      return new Date(lastUsed).toLocaleDateString();
    }
  }

  /**
   * Check if account needs attention (expired, needs reauth, etc.)
   */
  static needsAttention(account: StoredAccount): {
    needsAttention: boolean;
    reason?: string;
    severity: 'low' | 'medium' | 'high';
  } {
    const now = Date.now();
    const daysSinceLastUsed = (now - account.lastUsed) / (1000 * 60 * 60 * 24);

    // Check if account hasn't been used for a long time
    if (daysSinceLastUsed > 30) {
      return {
        needsAttention: true,
        reason: 'Account not used for over 30 days',
        severity: 'medium'
      };
    }

    // Check if persistence is about to expire
    const persistenceExpiryMs = account.lastUsed + (account.persistenceDays * 24 * 60 * 60 * 1000);
    const daysUntilExpiry = (persistenceExpiryMs - now) / (1000 * 60 * 60 * 24);

    if (daysUntilExpiry < 1 && daysUntilExpiry > 0) {
      return {
        needsAttention: true,
        reason: 'Account expires in less than 24 hours',
        severity: 'high'
      };
    }

    if (daysUntilExpiry < 0) {
      return {
        needsAttention: true,
        reason: 'Account has expired',
        severity: 'high'
      };
    }

    return {
      needsAttention: false,
      severity: 'low'
    };
  }

  /**
   * Sort accounts by priority (active first, then by last used)
   */
  static sortAccountsByPriority(
    accounts: StoredAccount[], 
    activeAccountId: string | null
  ): StoredAccount[] {
    return accounts.sort((a, b) => {
      // Active account always first
      if (a.id === activeAccountId) return -1;
      if (b.id === activeAccountId) return 1;

      // Then by last used (most recent first)
      return b.lastUsed - a.lastUsed;
    });
  }

  /**
   * Filter accounts by search query
   */
  static filterAccounts(accounts: StoredAccount[], query: string): StoredAccount[] {
    if (!query.trim()) {
      return accounts;
    }

    const searchTerm = query.toLowerCase().trim();
    
    return accounts.filter(account => {
      const displayName = this.generateDisplayName(account).toLowerCase();
      const email = account.email.toLowerCase();
      const loginName = account.loginName.toLowerCase();

      return (
        displayName.includes(searchTerm) ||
        email.includes(searchTerm) ||
        loginName.includes(searchTerm)
      );
    });
  }

  /**
   * Group accounts by domain
   */
  static groupAccountsByDomain(accounts: StoredAccount[]): Record<string, StoredAccount[]> {
    const groups: Record<string, StoredAccount[]> = {};

    accounts.forEach(account => {
      const domain = account.email.includes('@') 
        ? account.email.split('@')[1] 
        : 'other';

      if (!groups[domain]) {
        groups[domain] = [];
      }
      groups[domain].push(account);
    });

    return groups;
  }
}